import React, { useEffect, useMemo } from 'react';

import { StepType } from '@reactour/tour';
import classNames from 'classnames';
import { has, isFunction } from 'lodash';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParam } from 'react-use';
import Icon from '../../../../../components/icon/Icon';
import { AMPLITUDE_EVENTS } from '../../../../../constants/analytics';
import { useRedirect } from '../../../../../hooks/useRedirect';
import {
  clearTourSelected,
  putTourStatus,
} from '../../../../../redux/actions/tour';
import routes from '../../../../../routes/constants';
import text from '../../../../../text';
import { amplitudeTrack } from '../../../../../utilities';
import { TOURS } from './config';

const { SITE_TOUR_ACTIVITY } = AMPLITUDE_EVENTS;

interface TourContentProps {
  steps: Steps[];
  currentStep: number;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
}
const TourContent: React.FC<TourContentProps> = ({
  steps,
  currentStep: initialStep,
  setIsOpen,
  setCurrentStep,
}) => {
  const totalSteps = steps.length;
  const currentStep = initialStep;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  const content = steps[currentStep]?.content;
  const customProps = steps[currentStep]?.customProps || {};
  const dispatch = useDispatch();
  const tourSelected = useSelector(
    (state: { tourReducer: { tourSelected: string } }) =>
      state.tourReducer.tourSelected
  );

  const redirect = useRedirect();

  const isDemo = useSearchParam('isDemo');

  const isFileManagerTour = useMemo(() => {
    return tourSelected === TOURS.TOUR_FILE_MANAGER;
  }, [tourSelected]);

  const handleNextButton = () => {
    const executeStep = () => {
      if (isLastStep) {
        setIsOpen(false);
      } else {
        setCurrentStep((s) => s + 1);
      }
    };

    if (
      has(customProps.callback, 'next') &&
      isFunction(customProps.callback.next)
    ) {
      customProps.callback.next();
    }

    if (has(customProps, 'stepDelay') && customProps.stepDelay && customProps.stepDelay > 0) {
      setTimeout(() => executeStep(), customProps.stepDelay + 10);
    } else {
      executeStep();
    }
  };

  const handlePrevButton = () => {
    if (
      has(customProps.callback, 'next') &&
      isFunction(customProps.callback.prev)
    ) {
      customProps.callback.prev();
    }

    if (has(customProps, 'stepDelay') && customProps.stepDelay && customProps.stepDelay > 0) {
      setTimeout(
        () => setCurrentStep((s) => s - 1),
        (customProps.stepDelay ?? 0) + 10
      );
    } else {
      setCurrentStep((s) => s - 1);
    }
  };

  useEffect(() => {
    console.log('DEBUG', content);
  }, [content]);

  const isNextHidden = customProps.hideNext || false;
  const isPrevHidden = customProps.hidePrev || false;

  const renderContent = () => {
    if (typeof content === 'function') {
      return (content as () => React.ReactNode)();
    }
    return content;
  };

  return (
    <div
      style={{ position: 'relative' }}
      className={classNames('TourContent p-3 px-4 with-caret', {
        [`caret-${customProps?.caret}`]: !!customProps && customProps.caret,
        [`caretPosY-${customProps?.caretPosY}`]:
          !!customProps && customProps.caretPosY,
      })}
    >
      <Icon
        icon='close'
        // @ts-expect-error Icon not typed
        onClick={() => {
          amplitudeTrack(SITE_TOUR_ACTIVITY.EVENT, {
            // TODO: convert amplitude events constant to typescript
            // @ts-expect-error CONSTANT not typed
            action: SITE_TOUR_ACTIVITY[tourSelected]['INC'],
          });
          dispatch(putTourStatus(tourSelected));
          dispatch(clearTourSelected());
          setIsOpen(false);

          if (isFileManagerTour && !!isDemo) {
            redirect.push(routes.fileManager.root);
          }
        }}
      />
      {renderContent()}
      <div className='d-flex flex-row justify-content-end' style={{ gap: 8 }}>
        {!isFirstStep && !isPrevHidden ? (
          <Button
            variant='light'
            className='mt-4 grey-2'
            onClick={handlePrevButton}
          >
            {text('previous')}
          </Button>
        ) : null}

        {!isNextHidden && (
          <Button className='mt-4' onClick={handleNextButton}>
            {`${text(isLastStep ? 'complete' : 'next')} (${
              currentStep + 1
            }/${totalSteps})`}
          </Button>
        )}
      </div>
    </div>
  );
};
export default TourContent;

interface StepContentProps {
  title: string;
  description?: string;
  externalLinkHelp?: string;
  children?: React.ReactNode;
}
export const StepContent: React.FC<StepContentProps> = ({
  title,
  description,
  externalLinkHelp,
  children,
}) => (
  <div>
    <h5 className='mb-3 green-1 mt-2'>{title}</h5>
    {description ? <p className='mb-0'>{description}</p> : null}
    {children}
    {externalLinkHelp ? (
      <span>
        (
        <a href={externalLinkHelp} target='_blank' rel='noopener noreferrer'>
          {text('learnMore')}
        </a>
        )
      </span>
    ) : null}
  </div>
);

export type Steps = StepType & {
  customProps: {
    hideNext?: boolean;
    hidePrev?: boolean;
    stepDelay?: number;
    callback?: {
      next: () => void;
      prev?: () => void;
    };
    caret?: 'left' | 'right' | 'top' | 'bottom';
    caretPosY?: 'sm' | 'md' | 'lg' | 'xl';
  };
};
